import { TextInput, Select, InputBase } from "@mantine/core";
import { IMaskInput } from "react-imask";
import { useForm, schemaResolver } from "@mantine/form";
import { z } from "zod";
import { PROVIDER_SPECIALTIES } from "../../data/provider-specialties";

import styles from "./user-provider-setup-wrapper.module.scss";

type ProviderUserFormProps = {
  formId: string;
  onSubmit: (formData: any) => Promise<void>;
};

export type ProviderUserFormValues = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  organizationName: string;
  organizationType: string;
  physicianNPI: string;
  specialty: string;
  advocacyGroupEIN: string;
};

export const providerOrganizationType = "Provider";
const advocacyOrganizationType = "Advocacy Group";

const schema = z
  .object({
    firstName: z.string().min(1, { message: "First name is required" }),
    lastName: z.string().min(1, { message: "Last name is required" }),
    jobTitle: z.string().min(1, { message: "Job title is required" }),
    organizationName: z
      .string()
      .min(1, { message: "Organization name is required" }),
    organizationType: z
      .string()
      .min(1, { message: "Organization type is required" }),
    physicianNPI: z.string().optional(),
    specialty: z.string().optional(),
    advocacyGroupEIN: z.string().optional(),
  })
  .superRefine((data, ctx): void => {
    if (data.organizationType === providerOrganizationType) {
      const physicianNPISchema = z
        .string()
        .length(10, { message: "NPI must be 10 digits" })
        .regex(/^\d+$/, { message: "NPI must contain only digits" });
      const npiResult = physicianNPISchema.safeParse(data.physicianNPI);
      if (!npiResult.success) {
        if (data.physicianNPI === "") {
          ctx.addIssue({
            code: "custom",
            message: "NPI is required",
            path: ["physicianNPI"],
          });
        } else {
          npiResult.error.issues.forEach((issue) =>
            ctx.addIssue({
              code: "custom",
              message: issue.message,
              path: ["physicianNPI"],
            }),
          );
        }
      }

      const physicianSpecialtySchema = z.string().min(1);
      const specialtyResult = physicianSpecialtySchema.safeParse(
        data.specialty,
      );
      if (!specialtyResult.success) {
        ctx.addIssue({
          code: "custom",
          message: "Specialty is required",
          path: ["specialty"],
        });
      }
    } else if (data.organizationType === advocacyOrganizationType) {
      const einSchema = z.string().regex(/^\d{2}-\d{7}$/, {
        message: "EIN must be in the format xx-xxxxxxx (e.g., 12-3456789)",
      });

      const einResult = einSchema.safeParse(data.advocacyGroupEIN);
      if (!einResult.success) {
        if (data.advocacyGroupEIN === "") {
          ctx.addIssue({
            code: "custom",
            message: "EIN is required",
            path: ["advocacyGroupEIN"],
          });
        } else {
          einResult.error.issues.forEach((issue) =>
            ctx.addIssue({
              code: "custom",
              message: issue.message,
              path: ["advocacyGroupEIN"],
            }),
          );
        }
      }
    }
  });

function ProviderUserForm({ formId, onSubmit }: ProviderUserFormProps) {
  const form = useForm<ProviderUserFormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      jobTitle: "",
      organizationName: "",
      organizationType: "",
      physicianNPI: "",
      specialty: "",
      advocacyGroupEIN: "",
    },
    validate: schemaResolver(schema, { sync: true }),
  });

  return (
    <form
      id={formId}
      onSubmit={form.onSubmit((values) => onSubmit(values))}
      className={styles.form}
    >
      <TextInput label="First Name" {...form.getInputProps("firstName")} />
      <TextInput label="Last Name" {...form.getInputProps("lastName")} />
      <TextInput label="Job Title" {...form.getInputProps("jobTitle")} />
      <TextInput
        label="Organization Name"
        {...form.getInputProps("organizationName")}
      />
      <Select
        label="Organization Type"
        placeholder="Select one"
        searchable
        data={[advocacyOrganizationType, providerOrganizationType]}
        {...form.getInputProps("organizationType")}
      />
      {form.values.organizationType === providerOrganizationType && (
        <>
          <Select
            label="Specialty"
            placeholder="Select One"
            searchable
            data={PROVIDER_SPECIALTIES}
            {...form.getInputProps("specialty")}
          />
          <TextInput
            label="NPI (National Provider Identifier) Number"
            {...form.getInputProps("physicianNPI")}
          />
        </>
      )}
      {form.values.organizationType === advocacyOrganizationType && (
        <InputBase
          label="EIN (Employer Identification Number)"
          {...form.getInputProps("advocacyGroupEIN")}
          component={IMaskInput}
          mask="00-0000000"
          placeholder="XX-XXXXXXX"
        />
      )}
    </form>
  );
}

export default ProviderUserForm;
